import { useEffect, useRef, useState } from "react";
import { ProjectChooser } from "./ProjectChooser";
import { resolveCreationTarget } from "./creation";
import { readStored, writeStored } from "./storage";
import { scopeKey, type ToolId } from "./types";
import { useToolScope } from "./useToolScope";
export function useTaskStart<T extends { id: string; name: string }>({
  projects,
  toolId,
  onSelect,
  onCreate,
  loading,
}: {
  projects: T[];
  toolId: ToolId;
  onSelect: (project: T) => void;
  onCreate: () => void;
  loading?: boolean;
}) {
  const scope = useToolScope(),
    key = scopeKey(scope);
  const [open, setOpen] = useState(false);
  const [preferred, setPreferred] = useState<string | undefined>();
  const pendingScope = useRef<string | null>(null);
  const mounted = useRef(false);
  const latestScope = useRef(key);
  const previousScope = useRef(key);
  latestScope.current = key;
  useEffect(() => {
    mounted.current = true;
    if (previousScope.current !== key) {
      setOpen(false);
      pendingScope.current = null;
      previousScope.current = key;
    }
    return () => {
      mounted.current = false;
    };
  }, [key]);
  const isCurrent = () =>
    mounted.current && !!key && latestScope.current === key;
  const select = (project: T) => {
    if (!isCurrent()) return;
    setOpen(false);
    pendingScope.current = null;
    writeStored(`lazysem_last_project:${key}:${toolId}`, project.id);
    onSelect(project);
  };
  const create = () => {
    if (!isCurrent()) return;
    setOpen(false);
    pendingScope.current = key;
    onCreate();
  };
  return {
    begin: () => {
      if (!isCurrent() || loading) return;
      const preferred = readStored(`lazysem_last_project:${key}:${toolId}`);
      const target = resolveCreationTarget(
        projects,
        typeof preferred === "string" ? preferred : null,
      );
      if (target.kind === "selected")
        select(projects.find((p) => p.id === target.projectId)!);
      else if (target.kind === "create-project") create();
      else {
        setPreferred(target.preferredProjectId);
        setOpen(true);
      }
    },
    created: (project: T) => {
      if (isCurrent() && pendingScope.current === key) select(project);
    },
    cancel: () => {
      pendingScope.current = null;
      if (mounted.current) setOpen(false);
    },
    chooser: (
      <ProjectChooser
        open={open}
        projects={projects}
        loading={loading}
        preferredProjectId={preferred}
        onChoose={(id) => {
          const p = projects.find((p) => p.id === id);
          if (p) select(p);
        }}
        onCreateProject={create}
        onClose={() => setOpen(false)}
      />
    ),
  };
}
